import { DAEMON_URL } from "../config";
import { HttpError } from "./errors";

export type AdminUser = {
  id: number;
  email: string;
  role: "admin" | "member";
  created_at: string;
  last_login_at: string | null;
  totp_verified: number;
  daily_usd_cents: number | null;
  monthly_usd_cents: number | null;
};

/**
 * Partial PATCH: pass only the field that changed. Values are USD dollars
 * (e.g. 5 for $5.00). Backend stores as integer cents.
 */
export async function setUserLimits(
  userId: number,
  patch: { dailyUsd?: number | null; monthlyUsd?: number | null },
): Promise<{ dailyCents: number | null; monthlyCents: number | null }> {
  return req<{ ok: boolean; dailyCents: number | null; monthlyCents: number | null }>(
    `/admin/users/${userId}/limits`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(patch),
    },
  );
}

export type CreatedUser = {
  id: number;
  email: string;
  role: "admin" | "member";
  tempPassword: string;
};

async function req<T>(path: string, init: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}${path}`, { ...init, credentials: "include" });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  const text = await res.text();
  let data: unknown;
  try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
  if (!res.ok) {
    const msg = (data as { error?: string })?.error ?? `http ${res.status}`;
    throw new HttpError(res.status, msg);
  }
  return data as T;
}

export async function listUsers(): Promise<AdminUser[]> {
  const j = await req<{ users: AdminUser[] }>("/admin/users", {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  return j.users;
}

export async function createUser(email: string, role: "admin" | "member"): Promise<CreatedUser> {
  return req<CreatedUser>("/admin/users", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, role }),
  });
}

export async function deleteUser(id: number): Promise<void> {
  await req<{ ok: boolean }>(`/admin/users/${id}`, { method: "DELETE" });
}

export async function setUserRole(id: number, role: "admin" | "member"): Promise<void> {
  await req<{ ok: boolean }>(`/admin/users/${id}/role`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ role }),
  });
}

export type AdminProject = {
  id: string;
  name: string;
  kind: "addon" | "tool" | "meta";
  assignedUserCount: number;
};

export async function listAllProjects(): Promise<AdminProject[]> {
  const j = await req<{ projects: AdminProject[] }>("/admin/projects", {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  return j.projects;
}

export async function getUserProjects(userId: number): Promise<string[]> {
  const j = await req<{ projectIds: string[] }>(`/admin/users/${userId}/projects`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  return j.projectIds;
}

export type UsageBucket = { cents: number };
export type UserUsage = { d1: UsageBucket; d7: UsageBucket; d30: UsageBucket };
export type UsageByEmail = Record<string, UserUsage>;

export async function listUserUsage(): Promise<UsageByEmail> {
  const j = await req<{ usage: UsageByEmail }>("/admin/users/usage", {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  return j.usage;
}

export async function setUserProjects(userId: number, projectIds: string[]): Promise<string[]> {
  const j = await req<{ projectIds: string[] }>(`/admin/users/${userId}/projects`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ projectIds }),
  });
  return j.projectIds;
}
